import { fromKopecksToRouble } from '@scripts/helpers';

import {
    AvailabilityFields,
    OfferAvailability,
    OfferAvailabilitySignals,
    OfferAvailabilityStatus,
    SchemaOfferAvailability,
} from './types';

const IN_STOCK: OfferAvailability = {
    status: 'in_stock',
    label: 'В наличии',
    canAddToBasket: true,
};

const OUT_OF_STOCK: OfferAvailability = {
    status: 'out_of_stock',
    label: 'Нет в наличии',
    canAddToBasket: false,
};

const PRE_ORDER: OfferAvailability = {
    status: 'pre_order',
    label: 'Под заказ',
    canAddToBasket: true,
};

const isFiniteNumber = (value: number | null | undefined): value is number =>
    typeof value === 'number' && Number.isFinite(value);

export const parseAvailabilityStatus = (value: string | null | undefined): OfferAvailabilityStatus | null => {
    if (value === 'in_stock' || value === 'pre_order' || value === 'out_of_stock') {
        return value;
    }

    return null;
};

const sanitizeQty = (value: number | null | undefined): number => {
    if (!isFiniteNumber(value) || value < 0) {
        return 0;
    }

    return value;
};

export const sanitizeMultiple = (raw: number | null | undefined): number => {
    if (!isFiniteNumber(raw) || raw <= 0) {
        return 1;
    }

    const converted = fromKopecksToRouble(raw);

    if (!converted || converted < 0) {
        return 1;
    }

    return converted;
};

export const getMaxSelectableQty = (
    maxOrderQty: number | null | undefined,
    multipleRaw: number | null | undefined
): number => {
    const max = sanitizeQty(maxOrderQty);
    const multiple = sanitizeMultiple(multipleRaw);

    if (max <= 0) {
        return 0;
    }

    return Math.floor(max / multiple) * multiple;
};

export const isQtyValid = (
    qty: number,
    multipleRaw: number | null | undefined,
    maxOrderQty: number | null | undefined
): boolean => {
    if (!isFiniteNumber(qty) || qty <= 0) {
        return false;
    }

    const multiple = sanitizeMultiple(multipleRaw);
    const maxSelectable = getMaxSelectableQty(maxOrderQty, multipleRaw);

    if (qty > maxSelectable) {
        return false;
    }

    const remainder = qty % multiple;

    return remainder === 0;
};

export const canAddToCart = (fields: AvailabilityFields): boolean => {
    const status = parseAvailabilityStatus(fields.availability_status);
    const maxQty = sanitizeQty(fields.max_order_qty);

    return (status === 'in_stock' || status === 'pre_order') && maxQty > 0;
};

export const getPurchasableOffer = <T extends AvailabilityFields>(offers: T[] | undefined): T | undefined => {
    const offer = offers?.[0];

    if (!offer || !canAddToCart(offer)) {
        return undefined;
    }

    return offer;
};

export const resolveOfferAvailability = (signals: OfferAvailabilitySignals): OfferAvailability => {
    const status = signals.availabilityStatus;
    const maxQty = sanitizeQty(signals.maxOrderQty);

    if ((status === 'in_stock' || status === 'pre_order') && maxQty > 0) {
        if (status === 'pre_order') {
            return PRE_ORDER;
        }

        return IN_STOCK;
    }

    return OUT_OF_STOCK;
};

export const toOfferSignals = (
    item: AvailabilityFields & {
        offers?: AvailabilityFields[];
    }
): OfferAvailabilitySignals => {
    const source = item.offers?.[0] ?? item;

    return {
        availabilityStatus: parseAvailabilityStatus(source.availability_status),
        orderableStockQty: sanitizeQty(source.orderable_stock_qty),
        maxOrderQty: sanitizeQty(source.max_order_qty),
        multiple: source.multiple ?? item.multiple ?? 0,
    };
};

export const toSchemaAvailability = (status: OfferAvailabilityStatus): SchemaOfferAvailability => {
    switch (status) {
        case 'in_stock':
            return 'https://schema.org/InStock';
        case 'pre_order':
            return 'https://schema.org/PreOrder';
        case 'out_of_stock':
            return 'https://schema.org/OutOfStock';
    }
};
